import React, { useCallback, useContext, useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { Close, DoneAll, DeleteOutline, NotificationsNone } from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import AppBarContext from 'context/AppBarContext';
import PillTabs from 'components/shared/tabs/PillTabs';
import ErrorBoundary from 'components/shared/error-boundary/ErrorBoundary';
import notificationApi from 'api/graphql/notification';
import notificationSlice from 'store/modules/notification';
import uiSlice from 'store/modules/ui';
import { createSnack } from 'common/utils/general';
import { getNotificationType, normalizeDatabaseNotification } from 'common/utils/notification';
import { expiredNotificationMessage, getNotificationExpiry, resolveNotificationPath } from 'common/utils/notificationNav';
import { NOTIFICATION_GROUPS, countForGroup } from 'common/constants/notificationGroups';

const PAGE_SIZE = 20;

// Query propia de la página: incluye `type` (la del saga del menú no lo pide) y filtra por
// grupo en el SERVIDOR (`types`), no en cliente: con 20 por página, filtrar aquí dejaría las
// pestañas de Noticias y Ranking casi vacías aunque hubiera de sobra más atrás.
const NOTIFICATIONS_PAGE = gql`
  query notificationsCenter($first: Int!, $page: Int!, $user_id: ID!, $types: [String!]) {
    notifications(
      first: $first
      page: $page
      user_id: $user_id
      status: ALL
      types: $types
      orderBy: { column: CREATED_AT, order: DESC }
    ) {
      paginatorInfo {
        currentPage
        hasMorePages
        total
      }
      data {
        id
        created_at
        data
        read
        type
        user_id
      }
    }
    notificationTypeCounts(user_id: $user_id) {
      type
      total
    }
  }
`;

/**
 * Centro de notificaciones (/notifications). Agrupado por CATEGORÍA con contador —Todas,
 * Noticias, Ranking— en vez de por estado leído: es lo que el usuario pidió replicando
 * MySchoolUAE. Cada fila se puede borrar sin abrirla, y las que apuntan a un evento que ya
 * pasó avisan en lugar de llevarte a una pantalla vacía.
 */
function NotificationsPage() {
  const { t } = useTranslation(['routes', 'notifications', 'buttons']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const pageTitle = t('notifications.center', { ns: 'routes', defaultValue: 'Notificaciones' });

  const user = useSelector((state) => state.auth.user);
  const [groupIndex, setGroupIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const group = NOTIFICATION_GROUPS[groupIndex];

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const { data, loading, refetch } = useQuery(NOTIFICATIONS_PAGE, {
    variables: { first: PAGE_SIZE, page, user_id: user?.id, types: group.types },
    fetchPolicy: 'network-only',
    skip: !user?.id
  });

  const rows = data?.notifications?.data || [];
  const info = data?.notifications?.paginatorInfo || null;
  const typeCounts = data?.notificationTypeCounts || [];

  const refreshBadge = useCallback(() => {
    // El id va PLANO: la saga se lo pasa tal cual a la query, y envolverlo en un objeto
    // rompía la coerción de la variable `ID!` y disparaba el toast de error genérico.
    dispatch(notificationSlice.actions.countUnreadNotifications(user?.id));
  }, [dispatch, user?.id]);

  const notify = (variant, message) =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `notif-${Date.now()}`, variant, message })));

  const handleItemClick = async (row) => {
    const { expired, kind } = getNotificationExpiry(row);

    if (!row.read) {
      try {
        await notificationApi.markAsRead(row.id);
        refreshBadge();
      } catch {
        // Marcar leída es best-effort: la navegación no se bloquea.
      }
    }

    // Evento ya cumplido: avisar en vez de dejar al usuario en una pantalla vacía.
    if (expired) {
      notify('warning', expiredNotificationMessage(t, kind));
      await refetch();
      return;
    }

    const path = resolveNotificationPath(row, user?.role);
    if (path) navigate(path);
    else await refetch();
  };

  const handleDeleteOne = async (id) => {
    try {
      await notificationApi.remove(id);
      refreshBadge();
      await refetch();
    } catch {
      notify('error', t('deleteError', { ns: 'notifications', defaultValue: 'No pudimos eliminar la notificación.' }));
    }
  };

  const handleMarkAll = async () => {
    setBusy(true);
    try {
      await notificationApi.markAllAsRead(user.id);
      refreshBadge();
      await refetch();
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAll = async () => {
    setBusy(true);
    try {
      await notificationApi.deleteAll(user.id);
      refreshBadge();
      setPage(1);
      await refetch();
    } finally {
      setBusy(false);
    }
  };

  // Contador de la pestaña: apilado bajo el texto para que 2-3 dígitos no desborden la píldora.
  const tabLabel = (item) => {
    const total = countForGroup(typeCounts, item);
    return (
      <Stack alignItems="center" spacing={0.15}>
        <span>{t(item.labelKey, { ns: 'notifications', defaultValue: item.defaultLabel })}</span>
        {total > 0 && (
          <Box
            component="span"
            sx={{
              fontSize: '0.65rem',
              fontWeight: 800,
              lineHeight: 1.6,
              px: 0.75,
              borderRadius: 999,
              backgroundColor: 'surface.overlay',
              color: 'text.secondary',
              fontVariantNumeric: 'tabular-nums'
            }}>
            {total > 99 ? '99+' : total}
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <SEOPageTitle title={pageTitle} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 2.5 }}>
        <PillTabs
          value={groupIndex}
          onChange={(_, v) => {
            setGroupIndex(v);
            setPage(1);
          }}
          ariaLabel={pageTitle}
          tabs={NOTIFICATION_GROUPS.map((item) => ({ key: item.key, label: tabLabel(item) }))}
        />
        <Stack direction="row" spacing={1} sx={{ flex: 'none' }}>
          <Button
            size="small"
            startIcon={<DoneAll sx={{ fontSize: 16 }} />}
            onClick={handleMarkAll}
            disabled={busy}
            sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 700 }}>
            {t('markAllAsRead', { ns: 'notifications', defaultValue: 'Marcar todas' })}
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
            onClick={handleDeleteAll}
            disabled={busy}
            sx={{ color: 'sessionStatus.absent.fg', textTransform: 'none', fontWeight: 700 }}>
            {t('deleteAll', { ns: 'notifications', defaultValue: 'Vaciar' })}
          </Button>
        </Stack>
      </Stack>

      {loading && rows.length === 0 && (
        <Stack spacing={1.25}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={76} sx={{ borderRadius: '14px' }} />
          ))}
        </Stack>
      )}

      {!loading && rows.length === 0 && (
        <Box
          sx={{
            py: 8,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'hairline.main',
            borderRadius: 3,
            color: 'text.secondary'
          }}>
          <NotificationsNone sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="body1" fontWeight={700}>
            {t('emptyTitle', { ns: 'notifications', defaultValue: 'Nada por aquí' })}
          </Typography>
          <Typography variant="body2">
            {t('emptyBody', { ns: 'notifications', defaultValue: 'Cuando pase algo importante lo verás en esta bandeja.' })}
          </Typography>
        </Box>
      )}

      <Stack spacing={1.25}>
        {rows.map((row) => {
          const normalized = normalizeDatabaseNotification(row);
          const content = getNotificationType(normalized);
          if (!content) return null;
          const { expired } = getNotificationExpiry(normalized);

          return (
            <Stack
              key={row.id}
              direction="row"
              alignItems="flex-start"
              spacing={0.5}
              sx={{
                position: 'relative',
                pl: 2.5,
                pr: 0.75,
                py: 1.75,
                borderRadius: '14px',
                border: '1px solid',
                borderColor: 'hairline.main',
                backgroundColor: normalized.read ? 'surface.content' : 'surface.raised',
                // Lo caducado se atenúa: sigue siendo consultable, pero deja de competir.
                opacity: expired ? 0.6 : 1,
                transition: 'background-color 200ms cubic-bezier(0.32,0.72,0,1), transform 200ms cubic-bezier(0.32,0.72,0,1)',
                '&:hover': { backgroundColor: 'surface.hover' },
                // Punto dorado de "no leída".
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: normalized.read ? 'transparent' : 'gold.main'
                }
              }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => handleItemClick(normalized)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleItemClick(normalized);
                  }
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  cursor: 'pointer',
                  '&:active': { transform: 'scale(0.99)' },
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: 2, borderRadius: '10px' }
                }}>
                {/* Una notificación vieja con data malformada no debe tumbar toda la bandeja. */}
                <ErrorBoundary name={`notification-row-${row.id}`} fallback={() => null}>
                  {content}
                </ErrorBoundary>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteOne(row.id);
                }}
                aria-label={t('deleteOne', { ns: 'notifications', defaultValue: 'Eliminar notificación' })}
                sx={{ flex: 'none', color: 'text.disabled', '&:hover': { color: 'sessionStatus.absent.fg' } }}>
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>

      {info && info.total > 0 && (
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {t('showingCount', {
            ns: 'notifications',
            shown: rows.length,
            total: info.total,
            defaultValue: `Mostrando ${rows.length} de ${info.total}`
          })}
        </Typography>
      )}

      {info && (info.hasMorePages || page > 1) && (
        <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 1.5 }}>
          <Button
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 700 }}>
            {t('previous', { ns: 'buttons', defaultValue: 'Anterior' })}
          </Button>
          <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {page}
          </Typography>
          <Button
            size="small"
            disabled={!info.hasMorePages || loading}
            onClick={() => setPage((p) => p + 1)}
            sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 700 }}>
            {t('next', { ns: 'buttons', defaultValue: 'Siguiente' })}
          </Button>
        </Stack>
      )}
    </Container>
  );
}

export default NotificationsPage;
